import Image, { StaticImageData } from 'next/image'
import { Trait } from './Trait'
import { TraitsWrapper } from './TraitsWrapper'
import { cn } from '@utils/index'
import LoadingDeWi from '@assets/dewi_loading.gif'
import { useCallback, useMemo, useRef } from 'react'
import useAuctionManager from '@hooks/useAuctionManager'
import { useSolana } from '@dewicats/connect-button'

type DewiAuctionCardProps = {
  traits?: { value: string; trait_type: string }[]
  image?: string | StaticImageData
  invisible?: boolean
  className?: string
  placeholder?: boolean
}

export const DewiAuctionCard = ({
  traits,
  image,
  invisible = false,
  className,
  placeholder = false,
}: DewiAuctionCardProps) => {
  const { currentListing, currentCat } = useAuctionManager()
  const { anchorProvider } = useSolana()

  const isDevnet = useMemo(() => {
    return anchorProvider?.connection.rpcEndpoint.includes('devnet')
  }, [anchorProvider])

  const imageRef = useRef<HTMLImageElement>(null)
  const realTraits = useMemo(() => {
    if (!traits?.length) return
    const t = traits

    // fing trait with aura
    const aura = t.find((trait) => trait.trait_type.toLowerCase() === 'aura')
    if (aura) {
      // Move Aura to the beginning
      t.splice(t.indexOf(aura), 1)
      t.unshift(aura)
    }

    return t
  }, [traits])

  const openExplorer = useCallback(() => {
    if (currentListing) {
      window.open(
        `https://explorer.solana.com/address/${currentListing?.nft.toBase58()}`,
        '_blank',
      )
    } else if (currentCat) {
      window.open(
        `https://explorer.solana.com/address/${currentCat?.id}`,
        '_blank',
      )
    }
  }, [currentListing, currentCat])

  return (
    <div
      className={cn(
        'shadow-lg h-fit w-60 flex flex-col gap-4 rounded-sm p-2',
        invisible
          ? 'bg-gradientGray scale-95 border-[1px] border-dark-6'
          : 'bg-dark-7 border-[1px] border-dark-6',
        className,
      )}
    >
      {placeholder ? (
        <div className="h-[30rem]"></div>
      ) : (
        <>
          <div className="relative cursor-pointer" onClick={openExplorer}>
            <Image
              alt="Current DeWiCat"
              src={image}
              width={300}
              height={300}
              onLoad={(event) => {
                event.currentTarget.style.opacity = '1'
                imageRef.current.style.opacity = '0'
              }}
              onError={(event) => {
                event.currentTarget.style.opacity = '0'
              }}
              className={cn('rounded relative z-20 opacity-0', {
                'invisible ': invisible,
              })}
            />
            <Image
              ref={imageRef}
              alt="Loading DeWiCat"
              src={LoadingDeWi}
              unoptimized={true}
              width={300}
              height={300}
              className={cn('rounded absolute inset-0', {
                'invisible ': invisible,
              })}
            />
          </div>
          <TraitsWrapper invisible={invisible}>
            {realTraits
              ?.slice(0, Math.min(traits.length, 5))
              ?.map((trait: { value: string; trait_type: string }) => (
                <Trait key={trait.trait_type} {...trait} />
              ))}
          </TraitsWrapper>
        </>
      )}
    </div>
  )
}
